<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
import { useWidgetRangeStore } from '~/store/widgetRangeStore';

const props = defineProps<{
  apiData: any;
  property?: any;
  // 지열 시스템 목록 (임시 데이터 또는 props)
  geothermalSystems?: Array<{ id: string | number; name: string; location?: string | null; data: any }>;
}>();

const tocStore = useTocStore();
const equipStore = useEquipStore();
const widgetRangeStore = useWidgetRangeStore();


const emits = defineEmits<{
  (e: 'callApi', params?: Record<string, any>): void;
}>();

// 탭 네비게이션 관련
const selectedSystemId = ref<string | number>();
const selectedSystemLocation = ref<string | null>();
const tabListWrapper = ref<HTMLElement>();
const tabList = ref<HTMLElement>();
const scrollPosition = ref(0);
const requestId = ref(0);
const tabListVersion = ref(0); // geothermalSystems 변경 후 DOM 반영 시점을 추적하기 위한 트리거

// 계통도 이미지 관련
const diagramContentRef = ref<HTMLElement>();
const diagramImageRef = ref<HTMLImageElement>();
const imgWrapperRef = ref<HTMLElement>();
const overlayStyle = ref({
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%'
});

// 화살표 설정
const arrow1Color = ref('#bbbbbb'); // 첫 번째 화살표 색상 (기본값: 파란색)
const arrow1Direction = ref<'up' | 'down'>('up'); // 첫 번째 화살표 방향 (기본값: 위)
const arrow1Position = ref({ top: 89, left: 98.2 }); // 첫 번째 화살표 위치 (퍼센트 값)

const arrow2Color = ref('#bbbbbb'); // 두 번째 화살표 색상 (기본값: 초록색)
const arrow2Direction = ref<'up' | 'down'>('down'); // 두 번째 화살표 방향 (기본값: 아래)
const arrow2Position = ref({ top: 92, left: 93.2 }); // 두 번째 화살표 위치 (퍼센트 값)

// 화살표 크기 설정 (이미지 크기 대비 비율, 기본값: 0.25 = 25%)
const arrowSizeRatio = ref(0.2); // 화살표 기본 크기 비율 (0.1 = 10%, 0.25 = 25%, 0.5 = 50% 등)
const arrowWidthRatio = ref(0.5); // 화살표 너비 비율 (arrowBaseSize 대비, 기본값: 0.5 = 50%)
const arrowStrokeWidth = ref(7); // 화살표 막대(선) 두께 (기본값: 5, 이전: 3)

// 화살표 머리 부분 크기 설정
const arrowHeadSize = ref(20); // 화살표 머리 크기 (기본값: 16, 이전: 12)
const arrowHeadWidth = ref(20); // 화살표 머리 너비 (기본값: 10, 이전: 8)

// 화살표 marker ID (고유 ID 생성 - 한 번만 생성)
const arrow1MarkerId = ref(`arrowhead1-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
const arrow2MarkerId = ref(`arrowhead2-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

// 화살표 스타일 계산 (이미지 크기에 맞춰 조정)
const arrow1Style = ref({
  top: '88%',
  left: '98.4%',
  width: '50px',
  height: '60px'
});

const arrow2Style = ref({
  top: '90%',
  left: '93.3%',
  width: '50px',
  height: '60px'
});



// 임시 지열 시스템 목록 (API 개발 전까지 사용)
const defaultGeothermalSystems = ref<Array<{ id: string; name: string; location: string }>>([]);


// 지열 시스템 목록 (props 또는 기본값)
const geothermalSystems = computed(() => {
  return props.geothermalSystems || defaultGeothermalSystems.value;
});

// 탭 목록이 변경되면(DOM 반영 후) 좌우 화살표 활성 상태를 다시 계산
watch(geothermalSystems, () => {
  nextTick(() => {
    tabListVersion.value += 1;
  });
}, { deep: true });

// 스크롤 관련 computed
const canScrollLeft = computed(() => scrollPosition.value > 0);
const canScrollRight = computed(() => {
  tabListVersion.value; // geothermalSystems 변경 시 재계산되도록 의존성 추가
  if (!tabListWrapper.value || !tabList.value) return false;
  const wrapperWidth = tabListWrapper.value.clientWidth;
  const listWidth = tabList.value.scrollWidth;
  return scrollPosition.value < (listWidth - wrapperWidth);
});

// 스크롤 함수
function scrollLeft() {
  if (!tabListWrapper.value) return;
  const scrollAmount = tabListWrapper.value.clientWidth * 0.8;
  scrollPosition.value = Math.max(0, scrollPosition.value - scrollAmount);
  tabListWrapper.value.scrollTo({
    left: scrollPosition.value,
    behavior: 'smooth'
  });
}

function scrollRight() {
  if (!tabListWrapper.value || !tabList.value) return;
  const scrollAmount = tabListWrapper.value.clientWidth * 0.8;
  const maxScroll = tabList.value.scrollWidth - tabListWrapper.value.clientWidth;
  scrollPosition.value = Math.min(maxScroll, scrollPosition.value + scrollAmount);
  tabListWrapper.value.scrollTo({
    left: scrollPosition.value,
    behavior: 'smooth'
  });
}

// 탭 선택 함수
async function selectSystem(id: string | number, location: string | null) {
  selectedSystemId.value = id;
  selectedSystemLocation.value = location;
  const cid = String(id);
  requestId.value += 1;
  const currentRequestId = requestId.value;
  
  await equipStore.getSiteEquips(undefined, cid);
  widgetRangeStore.selectCid = cid;

  emits('callApi', { cid, _requestId: currentRequestId });

  // contents 영역의 데이터는 currentSystemData computed를 통해 자동 업데이트됨
  // 현재는 임시 데이터이므로 apiData를 그대로 사용
}

// 계통도 ON/OFF 상태 (API 데이터 또는 특정 값에 따라 결정)
// 예: data.value.diagramStatus, data.value.hp_status 등
const hp_status = computed(() => {
  if (!data.value) return true; // 기본값: ON
  // API 데이터에서 상태 확인 (필드명은 실제 API에 맞게 수정 필요)
  return data.value.hp_status === 0 ? false : true
});

const data = computed(() => {
  if(!props.apiData) return undefined;
  return props.apiData[0];
});

// API 응답 값을 자릿수 가공 없이 그대로 표시한다.
// (클라이언트에서 내림/반올림하면 35.3 이 35.29 처럼 원본과 달라질 수 있어 원본을 그대로 유지)
// 빈값만 '-' 로 대체한다.
function rawValue(value: any) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' && value.trim() === '') return '-';
  if (typeof value === 'number' && isNaN(value)) return '-';
  return value;
}

// 시스템 상태 데이터 추출
const hp_operation_rate = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_operation_rate ?? '-';
});

// const operatingStatus = computed(() => {
//   if (!data.value) return '-';
//   return data.value.operatingStatus ?? data.value.status ?? '-';
// });

const cop = computed(() => {
  if (!data.value) return '-';
  return data.value.cop ?? '-';
});

// 작동상태 가동률 데이터 추출
const heatPumpUtilization = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_operation_rate ?? '-';
});

const cp_operation_rate = computed(() => {
  if (!data.value) return '-';
  return data.value.cp_operation_rate ?? '-';
});

// 소비전력 현황 데이터 추출
const hp_usage = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_usage ?? '-';
});

const hp_total_usage_kwh = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_total_usage_kwh ?? data.value.hpCumulativePower ?? '-';
});

const cp_usage = computed(() => {
  if (!data.value) return '-';
  return data.value.cp_usage ?? '-';
});

const cp_total_usage_kwh = computed(() => {
  if (!data.value) return '-';
  return data.value.cp_total_usage_kwh ?? data.value.cpCumulativePower ?? '-';
});


// 히트펌프 용량 데이터 추출
const heatingCapacity = computed(() => {
  if (!data.value) return '-';
  return data.value.heatCapacity ?? '-';
});

const coolingCapacity = computed(() => {
  if (!data.value) return '-';
  return data.value.coolCapacity ?? '-';
});

// 지열시스템 데이터 추출
const hp_prod = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_prod ?? '-';
});

const hp_total_prod_kwh = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_total_prod_kwh ??  '-';
});


// 계통도 오버레이 데이터 추출
const hp_exit_temp = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_exit_temp ?? '-';
});

const hp_ent_temp = computed(() => {
  if (!data.value) return '-';
  return data.value.hp_ent_temp ?? '-';
});

// 출구온도 - 입구온도 차이(절대값)
const hp_temp_diff = computed(() => {
  if (!data.value) return '-';

  const exitTemp = Number(data.value.hp_exit_temp);
  const entTemp = Number(data.value.hp_ent_temp);

  if (data.value.hp_exit_temp === null || data.value.hp_exit_temp === undefined) return '-';
  if (data.value.hp_ent_temp === null || data.value.hp_ent_temp === undefined) return '-';
  if (isNaN(exitTemp) || isNaN(entTemp)) return '-';

  // 뺄셈 결과에는 부동소수점 잡음이 남으므로(예: 20.41 - 20.1 === 0.3099999999999987)
  // 값 자체를 계산 시점에 정규화한다. 표시 단계(commonUtils.isAllStrDataEmpty)에도 동일한 보정이 있지만,
  // 이 computed 값은 표시 외 용도로도 쓰일 수 있어 여기서 값을 확정한다.
  return Number(Math.abs(exitTemp - entTemp).toFixed(10));
});

const inputa = computed(() => {
  if (!data.value) return '-';
  return data.value.inputa ?? '-';
});

const cp_freq = computed(() => {
  if (!data.value) return '-';
  return data.value.cp_freq ?? '-';
});

const ch_status = computed(() => {
  if (!data.value) return '-';
  if(data.value.ch_status === 1){
    arrow1Color.value = '#fad054';
    arrow2Color.value = '#56cfff';
  } else if(data.value.ch_status === 2){
    arrow1Color.value = '#56cfff';
    arrow2Color.value = '#fad054';
  } else if(data.value.ch_status === 0){
    arrow1Color.value = '#bbbbbb';
    arrow2Color.value = '#bbbbbb';
  } else {
    arrow1Color.value = '#bbbbbb';
    arrow2Color.value = '#bbbbbb';
  }

  return data.value.ch_status;
});


// 온도차 오버레이 배경색 상태
// 냉/난방일 때는 계통도의 냉난방 표시와 같은 색, 그 외(정지/데이터 없음)에는 같은 표시의 정지 상태 색을 쓴다.
const tempDiffStatusClass = computed(() => {
  if (ch_status.value === 1) return 'temp-diff-overlay-cool';
  if (ch_status.value === 2) return 'temp-diff-overlay-heat';
  return 'temp-diff-overlay-off';
});

const flow = computed(() => {
  if (!data.value) return '-';
  return data.value.flow ?? '-';
});

const heatActivePower = computed(() => {
  if (!data.value) return '-';
  return data.value.heatActivePower ?? '-';
})

const coolActivePower = computed(() => {
  if (!data.value) return '-';
  return data.value.coolActivePower ?? '-';
})

const equipStatusName = computed(() => {
  if (!data.value) return '-';
  return equipStore.selectEquipInfo.equipStatusName ?? '-';
})

const equipStatusCode = computed(() => {
  if (!data.value) return '-';
  return equipStore.selectEquipInfo.equipStatus ?? '-';
})


// 운전상태 텍스트 변환
const getOperatingStatusText = (status: any) => {
  if (!status || status === '-') return '-';
  
  const statusMap: Record<string, string> = {
    '1': '운전중',
    '2': '정지',
    '3': '점검중',
    '4': '오류',
    'running': '운전중',
    'stopped': '정지',
    'maintenance': '점검중',
    'error': '오류',
  };
  
  return statusMap[String(status)] ?? String(status);
};

// 운전상태 색상
const getOperatingStatusColor = (status: any) => {
  if (!status || status === '-') return '#666';
  
  const statusStr = String(status).toLowerCase();
  if (statusStr === '1' || statusStr === 'running') return '#28a745'; // 운전중 - 녹색
  if (statusStr === '2' || statusStr === 'stopped') return '#6c757d'; // 정지 - 회색
  if (statusStr === '3' || statusStr === 'maintenance') return '#ffc107'; // 점검중 - 노란색
  if (statusStr === '4' || statusStr === 'error') return '#dc3545'; // 오류 - 빨간색
  
  return '#666';
};

onMounted(async () => {
  const rawCids = (tocStore.selectSiteInfo as any)?.cids as string | undefined;
  console.log(rawCids?.split(','));

  const splitCid = rawCids
    ? rawCids
        .split(',')
        .map((cid: string) => cid.trim())
        .filter((cid: string) => !!cid)
    : [];

  console.log(splitCid);
  
  defaultGeothermalSystems.value = [];

  for(let i = 0; i < splitCid.length; i++){
    const cid = splitCid[i];
    let equipInfos = await equipStore.getSiteEquips(undefined, cid);
    if(equipInfos){
      defaultGeothermalSystems.value.push({
        id: equipInfos.cid,
        name: equipInfos.equipName,
        location: equipInfos.equipLocation
      });
    }
  }
  const initLoc = await equipStore.getSiteEquips(undefined, splitCid[0] || equipStore.selectEquipInfo?.cid);
  
  const initialCid = splitCid[0] || equipStore.selectEquipInfo?.cid;
  const initialLocation = initLoc.equipLocatione || equipStore.selectEquipInfo?.equipLocation;

  if (initialCid) {
    await selectSystem(initialCid, initialLocation);
  } else if (geothermalSystems.value && geothermalSystems.value.length > 0) {
    selectedSystemId.value = geothermalSystems.value[0].id;
    selectedSystemLocation.value = geothermalSystems.value[0].location;
  }
  
  // 스크롤 위치 감지
  if (tabListWrapper.value) {
    tabListWrapper.value.addEventListener('scroll', () => {
      scrollPosition.value = tabListWrapper.value?.scrollLeft || 0;
    });
  }
  
  // 리사이즈 이벤트 리스너 추가
  // window.addEventListener('resize', handleResize);
  
  // ResizeObserver를 사용하여 컨테이너와 이미지 크기 변화 감지
  // ResizeObserver는 요소의 크기 변화를 정확하게 감지함
  let observerTimeout: ReturnType<typeof setTimeout> | null = null;
  const resizeObserver = new ResizeObserver((entries) => {
    // 여러 요소가 동시에 resize될 수 있으므로, 마지막 변경사항만 처리
    if (observerTimeout) {
      clearTimeout(observerTimeout);
    }
  });
  
  // 모든 관련 요소 관찰 시작
  if (diagramContentRef.value) {
    resizeObserver.observe(diagramContentRef.value);
  }
  
  if (imgWrapperRef.value) {
    resizeObserver.observe(imgWrapperRef.value);
  }
  
  if (diagramImageRef.value) {
    resizeObserver.observe(diagramImageRef.value);
  }


});

</script>

<template>
  <div class="system-status-card common-card" :style="{padding: '5px'}">
    <div class="card-wrapper">
      <!-- 첫 번째 Row: 좌측 Column(계통도 + 히트펌프 용량) + 오른쪽 Column(상태 정보) -->
      <div class="first-row">
        <!-- 좌측 Column: 계통도 + 히트펌프 용량 -->
        <div class="left-column">
              <!-- 탭 네비게이션 영역 (컴포넌트 내부 최상단) -->
    <div class="tab-navigation">
      <button 
        class="scroll-btn scroll-btn-left" 
        @click="scrollLeft"
        :disabled="!canScrollLeft"
      >
        ←
      </button>
      
      <div class="tab-list-wrapper" ref="tabListWrapper">
        <ul class="tab-list" ref="tabList">
          <li 
            v-for="(system, index) in geothermalSystems" 
            :key="system.id"
            :class="['tab-item', { active: selectedSystemId === system.id }]"
            @click="selectSystem(system.id, system.location ?? null)"
          >
            {{ system.name }}
          </li>
        </ul>
      </div>
      
      <button 
        class="scroll-btn scroll-btn-right" 
        @click="scrollRight"
        :disabled="!canScrollRight"
      >
        →
      </button>
    </div>
          <div class="diagram-area">
            <!-- <div class="diagram-header">
              <div class="area-title">계통도</div>
              <div class="diagram-status" :class="{ 'status-on': hp_status, 'status-off': !hp_status }">
                {{ hp_status ? 'ON' 
                 : 'OFF' }}
              </div>
            </div> -->
            <div class="diagram-content" ref="diagramContentRef">
              <!-- 계통도 배경 이미지 -->
              <div class="img-wrapper" ref="imgWrapperRef">
                <img 
                  ref="diagramImageRef"
                  src="@/assets/images/renewal/geo_new_flow_new4.png" 
                  alt="계통도"
                  class="diagram-image"
                />
              </div>
              
              <!-- 데이터 오버레이 + 화살표 (이미지 영역과 동일 크기/위치) -->
              <div 
                class="diagram-overlay"
                :style="overlayStyle"
              >
                <!-- 화살표 1 -->
                <svg 
                  v-if="data &&data.ch_status !== 0 && data.ch_status !== null"
                  class="arrow-overlay arrow-1" 
                  :class="{ 'arrow-up': arrow1Direction === 'up', 'arrow-down': arrow1Direction === 'down' }"
                  :style="{ 
                    '--arrow-color': arrow1Color,
                    ...arrow1Style
                  }"
                  viewBox="0 0 50 100"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <marker 
                      :id="arrow1MarkerId" 
                      :markerWidth="arrowHeadSize" 
                      :markerHeight="arrowHeadSize" 
                      :refX="arrowHeadSize / 2" 
                      :refY="arrowHeadSize / 2" 
                      orient="auto"
                      markerUnits="userSpaceOnUse"
                    >
                      <polygon 
                        :points="`0 0, ${arrowHeadWidth} ${arrowHeadSize / 2}, 0 ${arrowHeadSize}`" 
                        :fill="arrow1Color"
                        stroke="#000"
                        stroke-width="1.5"
                        stroke-linejoin="round"
                      />
                    </marker>
                  </defs>
                  <!-- 막대 부분 border (검은색 배경) -->
                  <line 
                    x1="25" 
                    y1="20" 
                    x2="25" 
                    y2="90" 
                    stroke="#000" 
                    :stroke-width="arrowStrokeWidth + 2" 
                    stroke-linecap="round"
                  />
                  <!-- 막대 부분 (원래 색상) -->
                  <line 
                    x1="25" 
                    y1="20" 
                    x2="25" 
                    y2="90" 
                    :stroke="arrow1Color" 
                    :stroke-width="arrowStrokeWidth" 
                    :marker-end="`url(#${arrow1MarkerId})`"
                    stroke-linecap="round"
                  />
                </svg>

                <!-- 화살표 2 -->
                <svg 
                  v-if="data && data.ch_status !== 0 && data.ch_status !== null"
                  class="arrow-overlay arrow-2" 
                  :class="{ 'arrow-up': arrow2Direction === 'up', 'arrow-down': arrow2Direction === 'down' }"
                  :style="{ 
                    '--arrow-color': arrow2Color,
                    ...arrow2Style
                  }"
                  viewBox="0 0 50 100"
                  preserveAspectRatio="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <marker 
                      :id="arrow2MarkerId" 
                      :markerWidth="arrowHeadSize"
                      :markerHeight="arrowHeadSize"
                      :refX="arrowHeadSize / 2" 
                      :refY="arrowHeadSize / 2" 
                      orient="auto"
                      markerUnits="userSpaceOnUse"
                    >
                      <polygon 
                        :points="`0 0, ${arrowHeadWidth} ${arrowHeadSize / 2}, 0 ${arrowHeadSize}`" 
                        :fill="arrow2Color"
                        stroke="#000"
                        stroke-width="1.5"
                        stroke-linejoin="round"
                      />
                    </marker>
                  </defs>
                  <!-- 막대 부분 border (검은색 배경) -->
                  <line 
                    x1="25" 
                    y1="20" 
                    x2="25" 
                    y2="90" 
                    stroke="#000" 
                    :stroke-width="arrowStrokeWidth + 2" 
                    stroke-linecap="round"
                  />
                  <!-- 막대 부분 (원래 색상) -->
                  <line 
                    x1="25" 
                    y1="20" 
                    x2="25" 
                    y2="90" 
                    :stroke="arrow2Color" 
                    :stroke-width="arrowStrokeWidth" 
                    :marker-end="`url(#${arrow2MarkerId})`"
                    stroke-linecap="round"
                  />
                </svg>

                <div class="overlay-item equip-ch_status" :class="`equip-ch-status-overlay-${ch_status}`">
                  <span v-if="ch_status === 1">{{ '냉방' }}</span>
                  <span v-else-if="ch_status === 2">{{ '난방' }}</span>
                  <span v-else-if="ch_status === 0">{{ '-' }}</span>
                  <span v-else>{{ '-' }}</span>
                </div>

                <!-- <div class="overlay-item equip-ch_status" :class="`equip-ch-status-overlay-0`">
                  <span>가동안함</span>1
                </div> -->

                <div class="overlay-item equip-status" :class="`equip-overlay-item-${equipStatusCode}`" :style="{left: '0', width: '65px'}">
                  <span>{{ equipStatusName }}</span>
                </div>

                <div class="overlay-item overlay-status-center">
                  <span>{{ selectedSystemLocation }}</span>
                </div>

                <!-- 운전상태 -->
                <div class="overlay-item diagram-status" :class="{ 'status-on': hp_status, 'status-off': !hp_status }" :style="{right: '0', width: '65px'}">
                  <span>{{ hp_status ? 'ON' : 'OFF' }}</span>
                </div>

                <!-- 1. 출구온도: 상단 파이프 위, 메인 유닛 오른쪽 약간 -->
                <div class="overlay-item overlay-outlet-temp overlay-item-value">
                  <span class="overlay-value">{{ rawValue(hp_exit_temp) }}°C</span>
                </div>

                <div class="overlay-item overlay-outlet-temp-label">
                  <span class="overlay-label">출구온도</span>
                </div>
                
                <!-- 2. 입구온도: 하단 파이프 위, 메인 유닛 오른쪽 약간 -->
                <div class="overlay-item overlay-inlet-temp overlay-item-value">
                  <span class="overlay-value">{{ rawValue(hp_ent_temp) }}°C</span>
                </div>

                <div class="overlay-item overlay-inlet-temp-label">
                  <span class="overlay-label">입구온도</span>
                </div>

                <!-- 2-1. 출구온도-입구온도 차이(절대값): x는 출구/입구온도, y는 순환펌프 값과 동일 -->
                <!-- 뺄셈 결과라 원본이 아니므로(35.3 - 28.9 = 6.399999999999999), 부동소수점 잡음 방지를 위해 자릿수 포맷 유지 -->
                <div class="overlay-item overlay-temp-diff" :class="tempDiffStatusClass">
                  <span class="overlay-value">Δ {{ commonUtils.isAllStrDataEmpty(hp_temp_diff, 2) }}°C</span>
                </div>

                <!-- 3. 전류: 메인 유닛 하단 섹션 중앙 -->
                <div class="overlay-item overlay-current overlay-item-value">
                  <span class="overlay-value">{{ rawValue(hp_operation_rate) }} %</span>
                </div>
                
                <!-- 4. 순환펌프 주파수: 순환펌프 위 -->
                <div class="overlay-item overlay-pump overlay-item-value">
                  <span class="overlay-value">{{ rawValue(cp_freq) }} Hz</span>
                </div>

                <div class="overlay-item overlay-pump-label">
                  <span class="overlay-label">순환펌프</span>
                </div>
                
                <!-- 5. 유량: 공기분리기 위, 값과 라벨 -->
                <div class="overlay-item overlay-flow overlay-item-value">
                  <span class="overlay-value">{{ rawValue(flow) }} m³/h</span>
                </div>

                <div class="overlay-item overlay-flow-label">
                  <span class="overlay-label">유량</span>
                </div>

                <!-- <div class="overlay-item overlay-air-label">
                  <span class="overlay-label">공기분리기</span>
                </div> -->
                
           
              </div>
            </div>
          </div>
        </div>
        
        <!-- 오른쪽 Column: 상태 정보 (시스템 부하율, COP, 작동상태 가동률, 소비전력 현황, 생산량) -->
        <div class="right-column">
          <!-- 상단 Row: 시스템 부하율, COP -->
          <!-- <div class="top-right-row"> -->
            <!-- 시스템 부하율 -->
            <!-- <div class="status-area status-area-1">
              <div class="area-label">시스템 부하율</div>
              <div class="area-value-wrapper">
                <div class="area-value">
                  <span class="value">{{ commonUtils.isAllStrDataEmpty(hp_operation_rate, 3) }}</span>
                  <span class="unit">%</span>
                </div>
              </div>
            </div> -->
            
            <!-- COP(성능계수) -->
            <!-- <div class="status-area status-area-3">
              <div class="area-label">COP(성능계수)</div>
              <div class="area-value-wrapper">
                <div class="area-value">
                  <span class="value cop-value">{{ commonUtils.isAllStrDataEmpty(cop, 3) }}</span>
                </div>
              </div>
            </div> -->
          <!-- </div> -->

          <!-- 히트펌프 용량 테이블: 계통도 하단부 -->
            <div class="table-area utilization-area main-wrapper-table" :style="{flex: '1'}">
              <label class="table-label">장비 사양</label>
            <div class="table-wrapper">
              <div class="table-item" :style="{height: '100%', padding: '10px'}">
                <table class="data-table" :style="{height: '50%'}">
                  <thead>
                    <tr>
                      <th :style="{backgroundColor: '#61CBF4', borderTopLeftRadius: '6px'}">히트펌프</th>
                      <th :style="{backgroundColor: '#CAEEFB'}">정미능력 (kW)</th>
                      <th :style="{backgroundColor: '#CAEEFB'}">유효전력 (kW)</th>
                      <th :style="{backgroundColor: '#CAEEFB', borderTopRightRadius: '6px'}">정격전류 (A)</th>
                    </tr>
                  </thead>
                  <tbody :style="{height: '100%'}">
                    <tr>
                      <td class="title-cell">냉방 / 난방</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.coolCapacity) : '-' }} / {{ data ? rawValue(data.heatCapacity) : '-' }}</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.coolActivePower) : '-' }} / {{ data ? rawValue(data.heatActivePower) : '-' }}</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.coolRatedCurrent) : '-' }} / {{ data ? rawValue(data.heatRatedCurrent) : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
                <table class="data-table" :style="{height: '50%'}">
                  <thead>
                    <tr>
                      <th :style="{backgroundColor: '#61CBF4'}">순환펌프</th>
                      <th :style="{backgroundColor: '#CAEEFB'}">유량 (m³/h)</th>
                      <th :style="{backgroundColor: '#CAEEFB'}">양정 (m)</th>
                      <th :style="{backgroundColor: '#CAEEFB'}">동력 (kW)</th>
                    </tr>
                  </thead>
                  <tbody :style="{height: '100%'}">
                    <tr>
                      <td class="title-cell">인라인펌프</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.geoWaterFLow) : '-' }}</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.cpPumpUpHead) : '-' }}</td>
                      <td class="value-cell fixed-value-cell">{{ data ? rawValue(data.cpCapacity) : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
             <!-- ?? -->
          <!-- <div class="table-area utilization-area-half">
            <div class="table-wrapper">
         
            </div>
          </div> -->
      
  

          <!-- 작동상태 가동률 -->
          <div class="table-area utilization-area" :style="{flex: '1.3'}">
            <label class="table-label">운전 상태</label>
            <div class="table-wrapper" :style="{height: '100%', padding: '10px'}">
              <table class="data-table" :style="{height: '100%'}">
                <colgroup>
                  <col width="30%"></col>
                  <col width="30%"></col>
                  <col width="30%"></col>
                </colgroup>
                <thead>
                  <tr>
                    <th :style="{backgroundColor: '#8ED973', borderTopLeftRadius: '6px'}">소비전력</th>
                    <th :style="{backgroundColor: '#D9F2D0'}">히트펌프</th>
                    <th :style="{backgroundColor: '#D9F2D0', borderTopRightRadius: '6px'}">순환펌프</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="title-cell">순간 (kWh)</td>
                    <td class="value-cell changed-value-cell">{{ data ? rawValue(data.hp_usage) : '-' }}</td>
                    <td class="value-cell changed-value-cell">{{ data ? rawValue(data.cp_usage) : '-' }}</td>
                  </tr>
                  <!-- 누계는 kWh → MWh 환산(나눗셈) 결과라 원본이 아니므로, 부동소수점 잡음 방지를 위해 자릿수 포맷 유지 -->
                  <tr>
                    <td class="title-cell">누계 (MWh)</td>
                    <td class="value-cell changed-value-cell">{{ data ? commonUtils.isAllStrDataEmpty(data.hp_total_usage_kwh/1000, 3) : '-' }}</td>
                    <td class="value-cell changed-value-cell">{{ data ? commonUtils.isAllStrDataEmpty(data.cp_total_usage_kwh/1000, 3) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
              <table class="data-table" :style="{height: '100%'}">
                <colgroup>
                  <col width="30%"></col>
                  <col width="30%"></col>
                  <col width="30%"></col>
                </colgroup>
                <thead>
                  <tr>
                    <th :style="{backgroundColor: '#8ED973'}" colspan="2">에너지생산량</th>
                    <th :style="{backgroundColor: '#8ED973'}">COP (성능계수)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="title-cell">순간 (kWh)</td>
                    <td class="value-cell changed-value-cell">{{ data ? rawValue(data.hp_prod) : '-' }}</td>
                    <td rowspan="2" class="value-cell changed-value-cell">{{ rawValue(cop) }}</td>
                  </tr>
                  <!-- 누계는 kWh → MWh 환산(나눗셈) 결과라 원본이 아니므로, 부동소수점 잡음 방지를 위해 자릿수 포맷 유지 -->
                  <tr>
                    <td class="title-cell">누계 (MWh)</td>
                    <td class="value-cell changed-value-cell">{{ data ? commonUtils.isAllStrDataEmpty(data.hp_total_prod_kwh/1000, 3) : '-' }}</td>
                  </tr>
                </tbody>
              </table>
        
            </div>
      
          </div>
          
          <!-- 소비전력 현황 -->
          <!-- <div class="table-area power-area d-flex" :style="{flex: '0.78'}">
            <div class="table-row-area col-6">
              <div class="table-wrapper" :style="{height: '100%'}">
                <table class="data-table" :style="{height: '100%'}">
                  <colgroup>
                    <col width="40%"></col>
                    <col width="60%"></col>
                  </colgroup>
                  <thead>
                    <tr>
                      <th colspan="2">에너지생산량</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="title-cell">순간 (kWh)</td>
                      <td class="value-cell changed-value-cell">{{ data ? commonUtils.isAllStrDataEmpty(data.hp_prod, 3) : '-' }}</td>
                    </tr>
                    <tr>
                      <td class="title-cell">누계 (MWh)</td>
                      <td class="value-cell changed-value-cell">{{ data ? commonUtils.isAllStrDataEmpty(data.hp_total_prod_kwh/1000, 3) : '-' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="table-row-area col-6">
              <div class="table-wrapper" :style="{height: '100%'}">
                <table class="data-table" :style="{height: '100%'}">
                  <thead>
                    <tr>
                      <th>COP (성능계수)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td rowspan="2" class="value-cell changed-value-cell">{{ commonUtils.isAllStrDataEmpty(cop, 2) }}</td>
                    </tr>
                    <tr>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div> -->
          
          <!-- 생산량 -->
          <!-- <div class="table-area geothermal-area">
            <div class="area-title">생산량</div>
            <div class="table-wrapper">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>구분</th>
                    <th>순간(kW)</th>
                    <th>누계(MWh)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="section-label">지중생산량(열원측)</td>
                    <td>{{ commonUtils.isAllStrDataEmpty(hp_prod, 3) }}</td>
                    <td>{{ commonUtils.isAllStrDataEmpty(hp_total_prod_kwh, 3) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/design-toc/define_font.scss";
@import "@/assets/styles/design-toc/common-card.scss";

.system-status-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  // 테이블 행 높이는 내용에 맞게 자동 조절
  
  .common-card {
    padding: 0.5rem !important;
  }
  
  // 탭 네비게이션 영역
  .tab-navigation {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;
    flex-shrink: 0;
    width: 100%;
    min-width: 0;
    max-width: 100%;

    .scroll-btn {
      flex-shrink: 0;
      width: 30px;
      height: 30px;
      border: 1px solid #ddd;
      background: #fff;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      color: #333;
      transition: all 0.2s;
      
      &:hover:not(:disabled) {
        background: #f5f5f5;
        border-color: #007CE1;
        color: #007CE1;
      }
      
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
    
    .tab-list-wrapper {
      flex: 1;
      min-width: 0;
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; // Firefox
      -ms-overflow-style: none; // IE/Edge
      
      &::-webkit-scrollbar {
        display: none; // Chrome/Safari
      }
      
      .tab-list {
        display: flex;
        gap: 0.25rem;
        list-style: none;
        padding: 0;
        margin: 0;
        white-space: nowrap;
        
        .tab-item {
          flex-shrink: 0;
          padding: 0.4rem 1rem;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          @include text-normal-12;
          font-size: 12px;
          color: #666;
          
          &:hover {
            background: #f5f5f5;
            border-color: #007CE1;
            color: #007CE1;
          }
          
          &.active {
            background: #007CE1;
            border-color: #007CE1;
            color: #fff;
            font-weight: 600;
          }
        }
      }
    }
  }
  
  .card-wrapper {
    background: transparent;
    border-radius: 6px;
    padding: 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    overflow: hidden;
  }
  
  // 첫 번째 Row: 좌측 Column(계통도 + 히트펌프 용량) + 오른쪽 Column(상태 정보)
  .first-row {
    display: flex;
    gap: 0.5rem;
    flex: 1;
    min-height: 0;
  }
  
  // 좌측 Column: 계통도 + 히트펌프 용량
  .left-column {
    flex: 1.26;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-height: 0;
    min-width: 0;
  }
  
  // 오른쪽 Column: 모든 상태 정보
  .right-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem; // 각 테이블 간 간격
    min-height: 0;
    overflow: visible; // 내용이 잘리지 않도록
  }
  
  // 오른쪽 Column 내부 상단 Row: 시스템 부하율, COP
  .top-right-row {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  
  // 각 영역 공통 스타일
  .status-area {
    flex: 1;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #ddd;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .area-label {
    @include text-normal-12;
    font-size: 11px;
    color: #666;
    font-weight: 500;
    text-align: left;
    flex-shrink: 0;
    margin-bottom: 0.25rem;
  }
  
  .area-value-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
  
  .area-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 0.25rem;
    
    .value {
      @include text-bold-14;
      font-size: 18px;
      color: #333;
      font-weight: 600;
      
      &.status-text {
        font-size: 14px;
        font-weight: 500;
      }
      
      &.cop-value {
        color: #007CE1;
        font-size: 20px;
      }
    }
    
    .unit {
      @include text-normal-12;
      font-size: 11px;
      color: #666;
    }
  }
  
  
  
  // 계통도 영역
  .diagram-area {
    flex: 1;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #ddd;
    padding: 0.5rem;
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-height: 0;
    
    .diagram-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.25rem;
      flex-shrink: 0;
    }
    
    .area-title {
      @include text-bold-12;
      font-size: 12px;
      color: #333;
      text-align: left;
    }
    
    .diagram-status {
      @include text-bold-16;
      font-size: 16px;
      font-weight: 700;
      padding: 0.2rem 1rem;
      border-radius: 20px;
      
      &.status-on {
        color: #16a34a;
        background: #bbf7d0;
      }
      
      &.status-off {
        color: #6b7280;
        background: #e5e7eb;
      }
    }
    
    .equip-status{
      @include text-bold-16;
      font-size: 16px;
      font-weight: 700;
      padding: 0.3rem 3rem !important;
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      
      span {
        display: block;
        line-height: 1.2;
        vertical-align: middle;
      }
      
      &.status-on {
        color: #16a34a;
        background: #bbf7d0;
      }
      
      &.status-off {
        color: #6b7280;
        background: #e5e7eb;
      }
    }

    .overlay-status-center {
      top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 0.3rem 0.5rem;
  span {
    @include text-bold-16;
    font-size: 14px;
    color: #333;
    font-weight: 600;
  }

    }
    
    .overlay-status-center {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      background: transparent;
      border: none;
      box-shadow: none;
      padding: 0.3rem 0.5rem;
      span {
        @include text-bold-16;
        font-size: 14px;
        color: #333;
        font-weight: 600;
      }
    }
    
    .diagram-content {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 0;
      width: 100%;
      position: relative;
      overflow: hidden;
      // 히트펌프 용량 테이블과 겹치지 않도록 하단 여백 확보
      box-sizing: border-box;
    }
    
    .img-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .diagram-image {
      width: 100%;
      height: 100%;
      object-position: center;
    }
    
    // 화살표 오버레이
    .arrow-overlay {
      position: absolute;
      pointer-events: none;
      z-index: 10;
      transform: translate(-50%, -50%);
      
      &.arrow-up {
        // 위 방향 화살표 (기본 - marker가 아래쪽에 있음)
        transform: translate(-50%, -50%) rotate(0deg);
      }
      
      &.arrow-down {
        // 아래 방향 화살표 (180도 회전)
        transform: translate(-50%, -50%) rotate(180deg);
      }
    }
    
    // 계통도 데이터 오버레이
    // 이미지의 실제 렌더링 영역에 정확히 맞춤
    .diagram-overlay {
      position: absolute;
      pointer-events: none;
      box-sizing: border-box;
    }
    
    

    .overlay-item {
      position: absolute;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      // gap: 0.03rem;
      padding: 0.1rem 0.3rem;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 15px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      white-space: nowrap;

      &.equip-overlay-item-normal{background-color: #32c44f;}
      &.equip-overlay-item-warning{background-color: #ffa800;}
      &.equip-overlay-item-fault{background-color: #ff0000;}
      &.equip-overlay-item-down{background-color: #5a5a5a;}
      &.equip-overlay-item-preparing{background-color: #0084ff;}

      &.overlay-item-value{
        background-color: #51e387;
      }

      &.overlay-status-center {
        background: transparent;
        border: none;
        border-radius: 0;
        box-shadow: none;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
      }
      
      .overlay-label {
        @include text-bold-12;
        font-size: 12px;
        color: #666;
        font-weight: 600;
        white-space: nowrap;
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .overlay-value {
        @include text-bold-12;
        font-size: 13px;
        color: #333;
        font-weight: 600;
        white-space: nowrap;
        line-height: 1.3;
        min-width: 80px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .equip-ch_status{
      @include text-bold-16;
      font-size: 16px;
      font-weight: 700;
      padding: 0.5rem;
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;

      left: 15.5%;
      width: 80px;
      top: 27%;
      transform: translate(-50%, -50%);

      &.equip-ch-status-overlay-1{
        background-color: #56cfff;
      }
      &.equip-ch-status-overlay-2{
        background-color: #fad054;
      }
      &.equip-ch-status-overlay-0{
        background-color: #bbbbbb;
      }
    }
    
    // 오버레이 위치 설정 (이미지 기준 정밀 배치)
    // 출구온도: 상단 파이프 위, 메인 유닛 오른쪽 약간 (상단 중앙에서 약간 왼쪽)
    .overlay-outlet-temp {
      top: 24%;
      left: 39.5%;
      transform: translateX(-50%);
    }
    .overlay-outlet-temp-label {
      top: 36%;
      left: 39.5%;
      transform: translateX(-50%);
    }
    
    
    // 입구온도: 하단 파이프 위, 메인 유닛 오른쪽 약간 (하단 중앙에서 약간 왼쪽)
    .overlay-inlet-temp {
      top: 73%;
      left: 39.5%;
      transform: translateX(-50%);
    }
    .overlay-inlet-temp-label {
      top: 85%;
      left: 39.5%;
      transform: translateX(-50%);
    }

    // 출구/입구온도 차이: x는 출구·입구온도와 동일, y는 순환펌프 값과 동일
    .overlay-temp-diff {
      top: 56%;
      left: 39.5%;
      transform: translateX(-50%);

      // 배경색은 계통도의 냉방·난방 표시(.equip-ch_status)와 동일하게 맞춘다.
      &.temp-diff-overlay-cool { // 냉방
        background-color: #56cfff;
      }
      &.temp-diff-overlay-heat { // 난방
        background-color: #fad054;
      }
      &.temp-diff-overlay-off { // 정지 / 데이터 없음
        background-color: #bbbbbb;
      }
    }

    // 전류: 메인 유닛 하단 섹션 중앙 (왼쪽 중앙, 약간 아래)
    .overlay-current {
      top: 59%;
      left: 15.5%;
      transform: translate(-50%, -50%);

      .overlay-value{
        font-size: 19px;
        
      }
    }
    
    // 순환펌프 주파수: 순환펌프 위 (우측 상단, 하단 파이프 위)
    .overlay-pump {
      top: 56%;
      left: 53.5%;
      transform: translateX(-50%);
    }
    .overlay-pump-label {
      top: 86%;
      left: 53.5%;
      transform: translateX(-50%);
    }
    
    
    // 유량: 공기분리기 위 (우측 하단, 순환펌프 오른쪽)
    .overlay-flow {
      top: 73%;
      left: 66%;
      transform: translateX(-50%);
      align-items: center;
    }
    .overlay-flow-label {
      top: 85%;
      left: 66%;
      transform: translateX(-50%);
      align-items: center;
    }
    
    .overlay-air-label {
      top: 83%;
      left: 20.1%;
      transform: translateX(-50%);
      align-items: center;
    }
    
    // 히트펌프 용량 테이블: 계통도 좌측 하단부
    .overlay-capacity-table {
      bottom: 2%;
      left: 6%;
      width: 70%;
      padding: 0.3rem;
      background: rgba(255, 255, 255, 0.98);
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      z-index: 10;
      
      .overlay-data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 11px;
        
        thead {
          background: #EFF6FF;
          
          th {
            @include text-normal-12;
            font-size: 8px;
            padding: 0.25rem 0.3rem;
            text-align: center;
            color: #464A53;
            font-weight: 700;
            border: none;
            border-bottom: 1px solid #ddd;
          }
        }
        
        tbody {
          tr {
            background: transparent;
            
            &:nth-child(even) {
              background: transparent;
            }
          }
          
          td {
            @include text-normal-12;
            font-size: 11px;
            padding: 0.2rem 0.3rem;
            text-align: center;
            color: #464A53;
            border: none;
            border-bottom: 1px solid #e5e5e5;
            
            &.value-cell {
              color: #007CE1;
              font-weight: 600;
            }
          }
        }
      }
    }
  }
  
  // 테이블 영역 공통 스타일
  .table-area {
    background: #fff;
    border-radius: 6px;
    border: 1px solid #ddd;
    display: flex;
    flex-direction: column; // 내부 테이블이 세로로 배치되도록
    flex: 1; // 균등하게 영역을 차지하도록
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%; // 너비를 100%로 설정하여 영역을 벗어나지 않도록
    box-sizing: border-box; // padding, border 포함하여 크기 계산
    min-height: 0; // flex 아이템이 축소될 수 있도록
    overflow: visible; // 내용이 잘리지 않도록

    .table-label{
        @include text-bold-14;
        padding: 10px 10px 0px 10px;
      }

    &.power-area{
      flex-direction: row;
    }

    .table-row-area {
      // padding: 0.05rem; // 최소 padding (거의 0)
      display: flex;
      flex-direction: column;
      // flex 제거 - 내용에 맞게 자동 조정
      // 높이 제한 제거 - 내용에 맞게 자동 조정

      &:nth-child(1){
        padding-right: 0.1rem;
        .table-wrapper{
          border-radius: 6px 0 0 0;
        }
      }
      &:nth-child(2){
        padding-left: 0.1rem;
        .table-wrapper{
          border-radius: 0 6px 0 0;
        }
      }
    }
    
    .area-title {
      @include text-bold-12;
      font-size: 12px;
      color: #333;
      margin-bottom: 0.25rem;
      flex-shrink: 0;
      text-align: left;
    }
    
    

    .table-wrapper {
      display: flex;
      flex-direction: column;
      align-items: stretch; // 영역을 꽉 채우도록
      justify-content: center; // 상단 정렬
      flex: 1; // 영역을 꽉 채우도록
      border-radius: 6px;
      width: 100%;
      // padding: 0.05rem; // 최소 padding (거의 0)
      box-sizing: border-box; // padding 포함하여 크기 계산
      gap: 0.25rem; // 테이블 간 간격
      overflow-y: auto; // 내용이 잘리지 않도록

    
    }
    
    .table-item {
      display: flex;
      flex-direction: column;
      flex: 0 1 auto; // 내용에 맞게 높이를 조절하되, 필요시 축소 가능
      overflow-y: auto; // 내용이 잘리지 않도록
    }
    
    .data-table {
      width: 100%;
      max-width: 100%; // 최대 너비 제한
      border-collapse: separate;
      border-spacing: 0;
      font-size: 11px;
      display: table;
      table-layout: fixed;
      // 테이블 높이는 내용에 맞게 자동 조절
      // 테이블 높이는 tr 개수에 따라 자동 조절, 모든 테이블의 행 높이를 동일하게 설정
      
      thead {
        background: #EFF6FF;
        display: table-header-group;
        
        th {
          @include text-normal-12;
          font-size: 13px;
          padding: 0.1rem 0.5rem;
          text-align: center;
          color: #464A53;
          font-weight: 700;
          border: none;
          height: 30px;
          border-bottom: 1px solid #ddd;
        }
      }
      
      tbody {
        display: table-row-group;
        // 높이 제한 제거 - tr 개수에 따라 자동 조절
        
        tr {
          background: transparent;
          display: table-row;
          // 높이는 내용에 맞게 자동 조절
          // height: 28px;
          
          &:nth-child(even) {
            background: transparent;
          }
          
          &.total-row {
            background: #EFF6FF;
          }
        }
        
        td {
          @include text-normal-12;
          font-size: 13px;
          // 높이는 내용에 맞게 자동 조절
          // padding: 0.4rem 0.5rem; // 적절한 padding으로 여백 확보
          padding: 0.1rem 0.2rem;
          text-align: center;
          color: #464A53;
          border: none;
          border-bottom: 1px solid #e5e5e5;
          background-color: #f7f7f7;
          display: table-cell;
          vertical-align: middle;
          box-sizing: border-box; // padding 포함하여 크기 계산
          
          &.fixed-value-cell {
            color: #11ac00;
            font-weight: 600;
          }

          &.changed-value-cell {
            color: #007CE1;
            font-weight: 600;
          }

          &.title-cell{
            font-weight: bold;
          }

          &.value-cell {
            font-size: 15px;
            font-weight: 600;
          }
        }
      }
    }
  }
  
  // 오른쪽 Column 내부 카드들 - 테이블 높이가 tr 개수에 따라 자동 조절
  // flex 제거됨 - space-between으로 동일한 간격 배치
  // 각 테이블의 크기는 내부의 tr 개수에 따라 변동됨
  
  // 히트펌프 용량 (left-column 또는 right-column에 사용)
  .capacity-area {
    flex-shrink: 0;
  }
  
  // 오른쪽 Column 내부: 생산량 (geothermal-area를 table-area로 변경)
  .geothermal-area {
    flex-shrink: 0;
    
    .section-label {
      @include text-bold-12;
      font-size: 11px;
      font-weight: 700;
      text-align: left;
      padding-left: 0.5rem;
    }
  }
}
</style>

